import { useEffect, useMemo, useRef, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { getCategoryBadgeClass, getCategoryLabel } from '@/lib/classColors'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export type ClassOption = {
  id: string
  code: string
  name: string
  label: string
  divisionName: string | null
  categoryCode?: string
  categoryName?: string
  statusLabel: string | null
  /** Listed so staff can see it, but it can't be chosen; statusLabel says why. */
  disabled?: boolean
}

export type ClassTypeaheadProps = {
  value: string
  onChange: (classId: string) => void
  options: ClassOption[]
  isLoading?: boolean
  disabled?: boolean
  placeholder?: string
  id?: string
  showCategoryFilters?: boolean
  emptyText?: string
}

export function ClassTypeahead({
  value,
  onChange,
  options,
  isLoading,
  disabled,
  placeholder,
  id,
  showCategoryFilters = false,
  emptyText = 'No classes match your search.',
}: ClassTypeaheadProps) {
  const selected = useMemo(
    () => options.find(option => option.id === value) ?? null,
    [options, value]
  )
  const [query, setQuery] = useState<string>(() => selected?.label ?? '')
  const inputRef = useRef<HTMLInputElement>(null)
  const [category, setCategory] = useState<string | null>(null)
  const categories = useMemo(() => {
    return Array.from(
      new Set(
        options.map(option => option.categoryCode).filter((code): code is string => Boolean(code))
      )
    ).map(code => ({ code, label: getCategoryLabel(code) }))
  }, [options])
  const listRef = useRef<HTMLUListElement>(null)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [open, setOpen] = useState(false)
  const listboxId = useMemo(() => (id ? `${id}-listbox` : undefined), [id])

  useEffect(() => {
    if (selected) {
      setQuery(selected.label)
    }
  }, [selected?.id, selected?.label])

  const filteredOptions = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    return options.filter(option => {
      if (showCategoryFilters && category && option.categoryCode !== category) return false
      if (!normalized) return true
      const haystack =
        `${option.code} ${option.name} ${option.divisionName ?? ''} ${option.categoryName ?? ''} ${option.categoryCode ?? ''}`.toLowerCase()
      return haystack.includes(normalized)
    })
  }, [options, query, category, showCategoryFilters])

  const showResults = open && !disabled && (query.length > 0 || options.length > 0)

  useEffect(() => {
    const list = listRef.current
    const option = list?.children[activeIndex] as HTMLElement | undefined
    if (!list || !option) return
    if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop
    else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight
    }
  }, [activeIndex])

  return (
    <div className="space-y-2">
      {showCategoryFilters && categories.length > 0 ? (
        <fieldset className="space-y-2" disabled={disabled || isLoading}>
          <legend className="text-sm font-medium">Category</legend>
          <div className="flex flex-wrap gap-2">
            {[{ code: null, label: 'All' }, ...categories].map(item => (
              <button
                key={item.code ?? 'all'}
                type="button"
                aria-pressed={category === item.code}
                className={cn(
                  'h-8 rounded-md border px-3 text-sm font-medium hover:brightness-95 dark:hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring disabled:opacity-50',
                  item.code ? getCategoryBadgeClass(item.code) : undefined,
                  category === item.code
                    ? 'ring-2 ring-foreground ring-offset-2 ring-offset-background'
                    : undefined
                )}
                onClick={() => {
                  setCategory(item.code)
                  setQuery('')
                  setActiveIndex(-1)
                  onChange('')
                  setOpen(true)
                  inputRef.current?.focus()
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </fieldset>
      ) : null}
      {showCategoryFilters ? (
        <label htmlFor={id} className="block text-sm font-medium">
          Class
        </label>
      ) : null}
      <div className="relative">
        <Input
          ref={inputRef}
          id={id}
          value={query}
          disabled={disabled}
          placeholder={isLoading ? 'Loading classes…' : placeholder}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showResults}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-activedescendant={
            showResults && filteredOptions[activeIndex]
              ? `${listboxId}-${filteredOptions[activeIndex].id}`
              : undefined
          }
          onFocus={() => {
            if (!disabled) setOpen(true)
          }}
          onChange={event => {
            const nextValue = event.target.value
            setQuery(nextValue)
            setActiveIndex(-1)
            if (selected && nextValue !== selected.label) {
              onChange('')
            }
            setOpen(true)
          }}
          onBlur={() => setOpen(false)}
          onKeyDown={event => {
            if (event.key === 'Escape') setOpen(false)
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault()
              setOpen(true)
              setActiveIndex(index => {
                const next = event.key === 'ArrowDown' ? index + 1 : index - 1
                return filteredOptions.length
                  ? (next + filteredOptions.length) % filteredOptions.length
                  : -1
              })
            }
            if (event.key === 'Enter' && showResults && filteredOptions[activeIndex]) {
              event.preventDefault()
              const option = filteredOptions[activeIndex]
              if (option.disabled) return
              onChange(option.id)
              setQuery(option.label)
              setOpen(false)
              setActiveIndex(-1)
            }
          }}
        />

        {showResults ? (
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-label="Available classes"
            className="absolute z-10 mt-2 max-h-64 w-full overflow-auto rounded-md border bg-popover p-1 text-sm shadow-md"
          >
            {filteredOptions.length === 0 ? (
              <li className="px-3 py-2 text-muted-foreground">{emptyText}</li>
            ) : (
              filteredOptions.map((option, index) => (
                <li
                  key={option.id}
                  id={`${listboxId}-${option.id}`}
                  data-testid={`class-option-${option.id}`}
                  role="option"
                  aria-selected={value === option.id}
                  aria-disabled={option.disabled || undefined}
                  className={cn(
                    'cursor-pointer rounded-sm px-3 py-2 hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
                    value === option.id || index === activeIndex
                      ? 'bg-accent text-accent-foreground'
                      : undefined,
                    option.disabled && 'cursor-not-allowed opacity-60'
                  )}
                  onMouseDown={event => {
                    event.preventDefault()
                    if (option.disabled) return
                    onChange(option.id)
                    setQuery(option.label)
                    setOpen(false)
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{option.label}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        {option.categoryCode ? (
                          <Badge
                            variant="secondary"
                            className={getCategoryBadgeClass(option.categoryCode)}
                          >
                            {getCategoryLabel(option.categoryCode, option.categoryName)}
                          </Badge>
                        ) : null}
                        <span>{option.code}</span>
                        {option.divisionName ? <span>{` · ${option.divisionName}`}</span> : null}
                      </div>
                    </div>
                    {option.statusLabel ? (
                      <Badge variant="outline" className="max-w-[45%] whitespace-normal text-right">
                        {option.statusLabel}
                      </Badge>
                    ) : null}
                  </div>
                </li>
              ))
            )}
          </ul>
        ) : null}
      </div>
    </div>
  )
}
